import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "spacer source" }
const _hoisted_2 = ["options", "value", "isLoading"]
const _hoisted_3 = {
  key: 0,
  class: "error-message"
}
const _hoisted_4 = {
  key: 0,
  class: "spacer source"
}
const _hoisted_5 = ["value", "label"]
const _hoisted_6 = {
  key: 1,
  class: "spacer source"
}
const _hoisted_7 = ["value", "label"]
const _hoisted_8 = {
  key: 0,
  class: "error-message"
}
const _hoisted_9 = {
  key: 2,
  class: "spacer source builder-image"
}
const _hoisted_10 = ["options", "value", "isLoading"]
const _hoisted_11 = {
  key: 1,
  class: "error-message"
}
const _hoisted_12 = ["value", "label", "placeholder", "disabled"]

import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import {
  APPLICATION_SOURCE_TYPE,
  APPLICATION_BUILD_MODE,
} from '../../types';
import { useBuilderImages } from '../../queries/useBuilderImagesQueries';
import { useAppCharts } from '../../queries/useAppChartsQueries';
import { ListResourceRequestParams, ResourceQueryOptions } from '../../models/resource/ui-types';
import { debounce } from 'lodash';
import { AppFormBuildOptions, AppFormDetails } from '../../models/application/ui-types';
import { isForbidden } from '../../utils/errors';


export default /*@__PURE__*/_defineComponent({
  __name: 'AppBuildOptions',
  props: {
    buildOptions: {},
    sourceType: {},
    mode: {},
    active: { type: Boolean },
    modalOpen: { type: Boolean },
    updateBuildOptions: { type: Function },
    updateDetails: { type: Function }
  },
  setup(__props: any) {


const store = useStore();

const t = store.getters['i18n/t'];

const props = __props;

watch(() => props.modalOpen, (newVal) => {
  appChartsRequestOptions.value.enabled = newVal;
  builderImageRequestOptions.value.enabled = newVal;
});

const dockerfilePathError = ref('');
const defaultBuilderImageForPlaceholder = ref('');

const appChartsRequestParams = ref<ListResourceRequestParams>({ page: 1, pageSize: 25, search: '' });
const appChartsRequestOptions = ref<ResourceQueryOptions>({ enabled: false, polling: false });
const {data: appChartsData, isLoading: isLoadingAppCharts, isError: isErrorAppCharts } = useAppCharts(store, appChartsRequestParams, appChartsRequestOptions);
const onAppChartsFilter = debounce((query: string) => {
  appChartsRequestParams.value.page = 1;
  appChartsRequestParams.value.search = query;
}, 500);


const builderImageRequestParams = ref<ListResourceRequestParams>({ page: 1, pageSize: 25, search: '' });
const builderImageRequestOptions = ref<ResourceQueryOptions>({ enabled: false, polling: false });
const {data: builderImagesData, isLoading: isLoadingBuilderImages, isError: isErrorBuilderImages, error: builderImagesError } = useBuilderImages(store, builderImageRequestParams, builderImageRequestOptions);
const onBuilderImageFilter = debounce((query: string) => {
  builderImageRequestParams.value.page = 1;
  builderImageRequestParams.value.search = query;
}, 500);

// If no app chart is set from the source or application configuration
// default to the standard app chart.
function setDefaultAppChart() {
  if (props.buildOptions.appChart) return;
  const items = appChartsData.value?.items;
  if (!items?.length) return;
  const standard = items.find(ac => ac.meta.name === 'standard');
  props.updateBuildOptions({
    appChart: standard?.meta.name || items[0].meta.name
  });
}
// If no builder image is set from the source, default to the catalog's default or
// its first entry. `custom` is a sentinel, not an image, so it is filtered out --
// seeding it would stage the literal string. Empty means the server picks.
const setDefaultBuilderImage = () => {
  if (props.buildOptions.builderImage) return;
  const defaultBuilderImage = builderImagesData.value?.items?.find((bi: any) => bi.default)?.image || '';
  defaultBuilderImageForPlaceholder.value = defaultBuilderImage;
  props.updateBuildOptions({ builderImage: defaultBuilderImage });
};
// Watch for changes to the app charts and builder images and set defaults accordingly
watch(appChartsData, setDefaultAppChart);
watch(builderImagesData, setDefaultBuilderImage);
watch(
  () => props.modalOpen,
  (isOpen) => {
    if (isOpen) {
      setDefaultAppChart();
      setDefaultBuilderImage();
    }
  }
);

// If builder images result in a forbidden error, update the build options so validation can
// account for it
watch(builderImagesError, (error) => {
  if (isForbidden(error)) {
    props.updateBuildOptions({ builderImagesForbidden: true });
  } else {
    props.updateBuildOptions({ builderImagesForbidden: false });
  }
});

// Format the builder images for the dropdown, adding a custom option
const allBuilderImages = computed(() => {
  const catalogImages = (builderImagesData.value?.items || []).map((bi) => ({
    value: bi.image,
    label: `${bi.meta.name} (${bi.shortDescription})`,
    default: bi.default
  }))
  const customOption = {
    value: 'custom',
    label: 'Custom',
    default: false
  }
  return [...catalogImages, customOption];
});

const selectedBuilderImage = computed(() => {
  return allBuilderImages.value.some(
    (bi) => bi.value === props.buildOptions.builderImage
  )
    ? props.buildOptions.builderImage
    : 'custom';
});

const isCustomBuilderImage = computed(
  () => selectedBuilderImage.value === 'custom'
);

// Validate the Dockerfile path input
function validateDockerfilePathValue(value: string): string {
  const trimmed = (value || '').trim();
  if (!trimmed) {
    dockerfilePathError.value = t('epinio.applications.steps.source.dockerfilePath.error.required');
  }
  if (
    trimmed.startsWith('/') ||
    trimmed.startsWith('\\') ||
    /^[A-Za-z]:[\\/]/.test(trimmed) ||
    trimmed.startsWith('\\\\')
  ) {
    dockerfilePathError.value = t('epinio.applications.steps.source.dockerfilePath.error.absolute');
  }

  const normalized = trimmed.replace(/\\/g, '/');
  if (normalized.split('/').some((part) => part === '..')) {
    dockerfilePathError.value = t('epinio.applications.steps.source.dockerfilePath.error.parent');
  }

  if (!/^[A-Za-z0-9._/-]+$/.test(normalized)) {
    dockerfilePathError.value = t('epinio.applications.steps.source.dockerfilePath.error.chars');
  }

  return dockerfilePathError.value || '';
}

const builderImageLabel = computed(() => props.buildOptions.builderImagesForbidden
  ? t('epinio.applications.steps.source.archive.builderimage.clusterDefault')
  : t('epinio.applications.steps.source.archive.builderimage.inputLabel'));

const showBuilderImage = computed(() =>
  [
    APPLICATION_SOURCE_TYPE.ARCHIVE,
    APPLICATION_SOURCE_TYPE.FOLDER,
    APPLICATION_SOURCE_TYPE.GIT_URL,
    APPLICATION_SOURCE_TYPE.GIT_HUB,
    APPLICATION_SOURCE_TYPE.GIT_LAB,
  ].includes(props.sourceType) &&
  props.buildOptions.buildMode === APPLICATION_BUILD_MODE.BUILDPACK
);

const showBuildMode = computed(() =>
  [
    APPLICATION_SOURCE_TYPE.ARCHIVE,
    APPLICATION_SOURCE_TYPE.FOLDER,
    APPLICATION_SOURCE_TYPE.GIT_URL,
    APPLICATION_SOURCE_TYPE.GIT_HUB,
    APPLICATION_SOURCE_TYPE.GIT_LAB,
  ].includes(props.sourceType)
);

const showDockerfilePath = computed(() => showBuildMode.value && props.buildOptions.buildMode === APPLICATION_BUILD_MODE.DOCKERFILE);

const buildModes = [
  { label: t('epinio.applications.steps.source.buildMode.buildpack'), value: APPLICATION_BUILD_MODE.BUILDPACK },
  { label: t('epinio.applications.steps.source.buildMode.dockerfile'), value: APPLICATION_BUILD_MODE.DOCKERFILE },
];

function handleBuilderImageDropdownChange(value: string) {
  if (value === 'custom') {
    props.updateBuildOptions({ builderImage: '' });
  } else {
    props.updateBuildOptions({ builderImage: value });
  }
}


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("trailhand-dropdown", {
        style: {"width":"100%"},
        options: (_unref(appChartsData)?.items || []).map((ac) => ({ value: ac.meta.name, label: `${ac.meta.name} (${ac.shortDescription})` })),
        value: _ctx.buildOptions.appChart,
        label: "Application Chart",
        placeholder: "Select an application chart",
        required: "",
        filterable: "",
        isLoading: _unref(isLoadingAppCharts),
        onDropdownChange: _cache[0] || (_cache[0] = (e) => { _ctx.updateBuildOptions({ appChart: e.detail.value }); }),
        onDropdownFilter: _cache[1] || (_cache[1] = (e) => { _unref(onAppChartsFilter)(e.detail.filter); })
      }, null, 40, _hoisted_2),
      _cache[7] || (_cache[7] = _createTextVNode()),
      (_unref(isErrorAppCharts))
        ? (_openBlock(), _createElementBlock("p", _hoisted_3, _toDisplayString(_unref(t)(`epinio.appCharts.errors.fetchAll`)), 1))
        : _createCommentVNode("", true)
    ]),
    _cache[15] || (_cache[15] = _createTextVNode()),
    (showBuildMode.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createElementVNode("h4", null, _toDisplayString(_unref(t)('epinio.applications.steps.source.buildMode.label')), 1),
          _cache[8] || (_cache[8] = _createTextVNode()),
          _createElementVNode("trailhand-dropdown", {
            style: {"width":"100%"},
            options: buildModes,
            value: _ctx.buildOptions.buildMode,
            required: "",
            "data-testid": "epinio_app-source_build-mode",
            label: _unref(t)('epinio.applications.steps.source.buildMode.inputLabel'),
            onDropdownChange: _cache[2] || (_cache[2] = (e) => { _ctx.updateBuildOptions({ buildMode: e.detail.value }); })
          }, null, 40, _hoisted_5)
        ]))
      : _createCommentVNode("", true),
    _cache[16] || (_cache[16] = _createTextVNode()),
    (showDockerfilePath.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
          _createElementVNode("h4", null, _toDisplayString(_unref(t)('epinio.applications.steps.source.dockerfilePath.label')), 1),
          _cache[9] || (_cache[9] = _createTextVNode()),
          _createElementVNode("trailhand-text-input", {
            style: {"width":"100%"},
            value: _ctx.buildOptions.dockerfilePath,
            "data-testid": "epinio_app-source_dockerfile-path",
            label: _unref(t)('epinio.applications.steps.source.dockerfilePath.inputLabel'),
            required: true,
            onTextInputChange: _cache[3] || (_cache[3] = (e) => { validateDockerfilePathValue(e.detail.value); _ctx.updateBuildOptions({ dockerfilePath: e.detail.value }); })
          }, null, 40, _hoisted_7),
          _cache[10] || (_cache[10] = _createTextVNode()),
          (dockerfilePathError.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_8, _toDisplayString(dockerfilePathError.value), 1))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    _cache[17] || (_cache[17] = _createTextVNode()),
    (showBuilderImage.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
          _cache[11] || (_cache[11] = _createElementVNode("h4", null, "Paketo Builder Image", -1)),
          _cache[12] || (_cache[12] = _createTextVNode()),
          (!_ctx.buildOptions.builderImagesForbidden)
            ? (_openBlock(), _createElementBlock("trailhand-dropdown", {
                key: 0,
                style: {"width":"100%"},
                options: allBuilderImages.value,
                value: selectedBuilderImage.value,
                label: "Builder Image",
                placeholder: "Select a builder image",
                required: "",
                filterable: "",
                isLoading: _unref(isLoadingBuilderImages),
                onDropdownChange: _cache[4] || (_cache[4] = (e) => { handleBuilderImageDropdownChange(e.detail.value) }),
                onDropdownFilter: _cache[5] || (_cache[5] = (e) => { _unref(onBuilderImageFilter)(e.detail.filter); })
              }, null, 40, _hoisted_10))
            : _createCommentVNode("", true),
          _cache[13] || (_cache[13] = _createTextVNode()),
          (_unref(isErrorBuilderImages))
            ? (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(_unref(t)(`epinio.builderImages.errors.fetchAll`)), 1))
            : _createCommentVNode("", true),
          _cache[14] || (_cache[14] = _createTextVNode()),
          _createElementVNode("trailhand-text-input", {
            style: {"width":"100%"},
            value: _ctx.buildOptions.builderImage,
            "data-testid": "epinio_app-source_builder-value",
            label: builderImageLabel.value,
            placeholder: defaultBuilderImageForPlaceholder.value,
            disabled: !isCustomBuilderImage.value,
            onTextInputChange: _cache[6] || (_cache[6] = (e) => { _ctx.updateBuildOptions({ builderImage: e.detail.value }); })
          }, null, 40, _hoisted_12)
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})